import React, { useState, useCallback } from 'react'

import { Icon, ChevronDownLine } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, number, oneOfType, shape, string,
} from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import DeleteConfirmModal from 'Pods/ThreeDStudio/Shared/DeleteConfirmModal/DeleteConfirmModal'

import { isPendingCommentId } from '../../constants'
import { formatAbsoluteDate, formatRelativeTime } from '../../Utils/formatDate'
import CommentRow from '../CommentRow/CommentRow'
import Composer from '../Composer/Composer'

// A top-level comment with its reply affordances: a meta row (date, Reply,
// Delete, replies toggle), an inline reply composer, and the collapsible replies.
function CommentThread({
  comment,
  currentUserId = null,
  locale = 'en',
  canComment = false,
  isSubmitting = false,
  onReply,
  onDelete,
}) {
  const t = useTranslation()
  const pageConfig = usePageConfig()
  const [showReplyForm, setShowReplyForm] = useState(false)
  const [repliesExpanded, setRepliesExpanded] = useState(false)
  // Deletion is optimistic and irreversible, so it goes through a confirm step.
  // Holds { id, itemType } for the comment or reply awaiting confirmation.
  const [pendingDelete, setPendingDelete] = useState(null)

  const replies = comment.replies || []
  const hasReplies = replies.length > 0
  const creatorLabel = t('commentsModal.creatorBadge')
  const repliesListId = `comments-modal-replies-${comment.id}`
  const replyFormId = `comments-modal-reply-form-${comment.id}`

  // Every thread renders a button reading just "Delete", so the accessible name
  // carries the author too - otherwise a screen reader's control list is a row of
  // identical entries. Keeps the visible word first (WCAG Label in Name), and
  // falls back to the bare label while an optimistic node has no author yet.
  const deleteLabel = useCallback((username, key) => (
    username ? t(key, { username }) : t('commentsModal.delete')
  ), [t])

  // The unit abbreviation and its spacing are per-locale ("30 min" vs "30分"), so
  // the whole string comes from i18n rather than being assembled here.
  const formatUnit = useCallback((unit, amount) => (
    t(`commentsModal.relativeTime.${unit}`, { count: amount })
  ), [t])

  // Nothing may act on a node until the create response gives it a server id:
  // Delete would 404, and a reply would carry the client-side temp id as its
  // parent. The composer would also be unmounted (draft and all) by the refetch
  // that replaces the pending node.
  const isPending = isPendingCommentId(comment.id)
  const canReply = canComment && !isPending
  const canDeleteComment = comment.author?.id != null
    && currentUserId != null
    && comment.author.id === currentUserId
    && !isPending

  const handleReplyClick = useCallback(() => {
    setShowReplyForm((prev) => {
      // Opening the composer reveals the thread it belongs to, so the new reply
      // lands in a list the user can already see.
      if (!prev) {
        setRepliesExpanded(true)
      }

      return !prev
    })
  }, [])

  const handleToggleReplies = useCallback(() => {
    setRepliesExpanded(prev => !prev)
  }, [])

  // Closing the composer is deferred until the server accepts the reply, so a
  // rejected one leaves the form open with the text still in it.
  const handleReplySubmit = useCallback(async (content) => {
    const accepted = await onReply(comment.id, content)

    if (accepted) {
      setShowReplyForm(false)
      setRepliesExpanded(true)
    }

    return accepted
  }, [onReply, comment.id])

  const handleDeleteComment = useCallback(() => {
    setPendingDelete({ id: comment.id, itemType: 'comment' })
  }, [comment.id])

  const handleDeleteReply = useCallback((replyId) => {
    setPendingDelete({ id: replyId, itemType: 'reply' })
  }, [])

  const handleConfirmDelete = useCallback(() => {
    onDelete(pendingDelete.id)
    setPendingDelete(null)
  }, [onDelete, pendingDelete])

  const handleCancelDelete = useCallback(() => {
    setPendingDelete(null)
  }, [])

  return (
    <li className="comments-modal__thread">
      <CommentRow author={comment.author} content={comment.content} creatorLabel={creatorLabel} layout="stacked" />
      <div className="comments-modal__thread-meta">
        <span className="comments-modal__comment-date cgt-caption">
          {formatAbsoluteDate(comment.createdAt, locale)}
        </span>
        {canReply && (
          <button
            type="button"
            className="comments-modal__link"
            aria-expanded={showReplyForm}
            aria-controls={replyFormId}
            onClick={handleReplyClick}
          >
            {t('commentsModal.reply')}
          </button>
        )}
        {canDeleteComment && (
          <button
            type="button"
            className="comments-modal__link"
            aria-label={deleteLabel(comment.author?.username, 'commentsModal.deleteCommentBy')}
            onClick={handleDeleteComment}
          >
            {t('commentsModal.delete')}
          </button>
        )}
        {hasReplies && (
          <button
            type="button"
            className="comments-modal__replies-toggle comments-modal__replies-toggle--count"
            aria-expanded={repliesExpanded}
            aria-controls={repliesListId}
            onClick={handleToggleReplies}
          >
            {t('commentsModal.repliesCount', { count: replies.length })}
          </button>
        )}
      </div>
      {showReplyForm && canReply && (
        <Composer
          id={replyFormId}
          variant="reply"
          placeholder={t('commentsModal.replyPlaceholder')}
          username={pageConfig?.currentUserUsername || ''}
          avatarUrl={pageConfig?.currentUserAvatarUrl || null}
          isSubmitting={isSubmitting}
          onSubmit={handleReplySubmit}
        />
      )}
      {repliesExpanded && hasReplies && (
        <ul id={repliesListId} className="comments-modal__replies">
          {replies.map((reply) => {
            const canDeleteReply = reply.author?.id != null
              && currentUserId != null
              && reply.author.id === currentUserId
              && !isPendingCommentId(reply.id)

            return (
              <li key={reply.id} className="comments-modal__reply">
                <CommentRow
                  author={reply.author}
                  content={reply.content}
                  creatorLabel={creatorLabel}
                  timeLabel={formatRelativeTime(reply.createdAt, { formatUnit })}
                  className="comments-modal__comment--reply"
                />
                {canDeleteReply && (
                  <button
                    type="button"
                    className="comments-modal__link comments-modal__link--reply-delete"
                    aria-label={deleteLabel(reply.author?.username, 'commentsModal.deleteReplyBy')}
                    onClick={() => handleDeleteReply(reply.id)}
                  >
                    {t('commentsModal.delete')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {repliesExpanded && hasReplies && (
        <button
          type="button"
          className="comments-modal__replies-collapse"
          aria-expanded={repliesExpanded}
          aria-controls={repliesListId}
          // Chrome computes no accessible name at all for this button from its
          // contents - the trailing chevron svg suppresses the "Hide" text node -
          // so the name is set explicitly. Matches the visible label (Label in Name).
          aria-label={t('commentsModal.hide')}
          onClick={handleToggleReplies}
        >
          <span className="comments-modal__replies-collapse-rule" aria-hidden="true" />
          {t('commentsModal.hide')}
          <Icon svgComponent={ChevronDownLine} size="small" notSpaced />
        </button>
      )}
      {pendingDelete && (
        <DeleteConfirmModal
          itemType={pendingDelete.itemType}
          onSubmit={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </li>
  )
}

CommentThread.propTypes = {
  comment: shape({
    id: oneOfType([number, string]),
    content: string,
    createdAt: string,
    author: shape({
      id: oneOfType([number, string]),
      username: string,
      avatarUrl: string,
      isAuthor: bool,
    }),
  }).isRequired,
  onReply: func.isRequired,
  onDelete: func.isRequired,
  currentUserId: oneOfType([number, string]),
  locale: string,
  canComment: bool,
  isSubmitting: bool,
}

export default CommentThread
